import React from 'react';

const SUPPORT_EMAIL = 'aevdir.com@gmail.com';
const EFFECTIVE_DATE_EN = 'April 25, 2026';
const EFFECTIVE_DATE_ZH = '2026 年 4 月 25 日';

const COPY = {
  en: {
    title: 'Privacy Policy',
    effective: `Effective date: ${EFFECTIVE_DATE_EN}`,
    intro:
      'This Privacy Policy explains how the AEVDIR team ("we", "our") handles information when you use the Wake Up Duck (Clock) iOS app and the aevdir.com website. We aim to collect as little as possible and to be explicit about what we do collect.',
    sections: [
      {
        heading: '1. Information we collect',
        body: [
          'Wake Up Duck (Clock) iOS app: alarm schedules, alarm sounds, snooze settings, and any in-app preferences are stored locally on your device. We do not maintain a copy of these on our servers.',
          'AEVDIR website (aevdir.com): if you choose to create an account, we store the user name, email and a salted password hash you provide. If you sign in we set a session token in your browser so you stay signed in.',
          'In-app subscriptions: subscription transactions are processed by Apple. We receive only the receipt / transaction information necessary to validate entitlement; we do not see your payment card.',
        ],
      },
      {
        heading: '2. Information we do NOT collect',
        body: [
          'No advertising identifiers (IDFA) and no third-party advertising SDKs.',
          'No analytics SDKs that profile individual users.',
          'No location data, microphone audio, contacts, photos, or health data.',
          'No tracking across other apps or websites.',
        ],
      },
      {
        heading: '3. How we use information',
        body: [
          'To provide core functionality: schedule and surface your alarms, remember your preferences, sign you in to the website.',
          'To validate in-app subscription entitlement.',
          'To respond to your support requests when you email us.',
          'To detect and prevent abuse of our service.',
        ],
      },
      {
        heading: '4. System permissions',
        body: [
          'AlarmKit (iOS 26+): required so the system can present your alarms reliably even in Silent or Focus mode. You can revoke this permission in iOS Settings at any time.',
          'Notifications (optional): used for snooze and alarm-related alerts. Revoke in iOS Settings → Notifications.',
        ],
      },
      {
        heading: '5. Data sharing',
        body: [
          'We do not sell or rent your personal data.',
          'We do not share data with advertising networks.',
          'We may share data only when required by law, or with service providers strictly necessary to operate the service (for example, our hosting provider). Such providers are bound to use the data only on our behalf.',
        ],
      },
      {
        heading: '6. Data retention',
        body: [
          'On-device data lives on your device until you delete the app or clear it from within the app.',
          'Website account data is retained while your account exists. You can request deletion at any time by emailing us.',
          'Support emails are retained only as long as needed to resolve your enquiry, and to comply with legal obligations.',
        ],
      },
      {
        heading: '7. Your rights',
        body: [
          'You have the right to access, correct, export or delete the personal data we hold about you. To exercise any of these rights, email us at the address below and we will respond within a reasonable timeframe.',
          'Depending on your jurisdiction (EEA, UK, California, etc.) you may have additional rights, such as the right to lodge a complaint with a supervisory authority.',
        ],
      },
      {
        heading: '8. Children',
        body: [
          'Wake Up Duck is not directed to children under the age of 13 (or the equivalent minimum age in your jurisdiction). We do not knowingly collect personal data from children. If you believe a child has provided us personal data, please contact us so we can delete it.',
        ],
      },
      {
        heading: '9. International transfers',
        body: [
          'Our infrastructure may be operated in regions different from your own. When we transfer personal data, we rely on contractual safeguards and only on providers that meet recognised security standards.',
        ],
      },
      {
        heading: '10. Security',
        body: [
          'We use HTTPS for all network traffic, salted password hashing for accounts, and standard server hardening. No system is perfectly secure; if you suspect an issue, please email us immediately.',
        ],
      },
      {
        heading: '11. Changes to this policy',
        body: [
          'We may update this policy occasionally. When we do, we will revise the effective date at the top of this page. Material changes will be highlighted on the website or in the app where appropriate.',
        ],
      },
      {
        heading: '12. Contact',
        body: [
          `Questions about this policy? Email us at ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
  },
  zh: {
    title: '隐私政策',
    effective: `生效日期：${EFFECTIVE_DATE_ZH}`,
    intro:
      '本隐私政策说明 AEVDIR 团队（"我们"）在你使用 醒醒鸭（Clock）iOS 应用 与 aevdir.com 网站时如何处理信息。我们坚持最小化收集，并对收集的内容保持明确说明。',
    sections: [
      {
        heading: '一、我们收集的信息',
        body: [
          '醒醒鸭（Clock）iOS 应用：闹钟时间表、铃声、稍后提醒设置以及应用内偏好仅保存在你的设备本地，我们的服务器不保留副本。',
          'AEVDIR 网站（aevdir.com）：如果你选择创建账户，我们会保存你提供的用户名、邮箱以及加盐后的密码哈希；登录时我们会在浏览器中设置一个会话 Token 以保持登录状态。',
          '应用内订阅：订阅交易由 Apple 处理。我们仅接收用于验证订阅权益所必需的票据/交易信息，不会看到你的支付卡信息。',
        ],
      },
      {
        heading: '二、我们不会收集的信息',
        body: [
          '不收集广告标识符（IDFA），不集成任何第三方广告 SDK。',
          '不集成对个体用户进行画像的分析 SDK。',
          '不收集位置、麦克风、通讯录、照片、健康等数据。',
          '不在其他应用或网站之间追踪你的行为。',
        ],
      },
      {
        heading: '三、信息的用途',
        body: [
          '提供核心功能：调度并呈现闹钟、保存偏好、登录网站。',
          '验证应用内订阅权益。',
          '当你通过邮件联系我们时，回复你的支持请求。',
          '识别与防范对服务的滥用行为。',
        ],
      },
      {
        heading: '四、系统权限',
        body: [
          'AlarmKit（iOS 26+）：用于让系统在静音或专注模式下也能稳定呈现闹钟。你可以随时在 iOS 设置中撤销该权限。',
          '通知（可选）：用于稍后提醒等闹钟相关提醒。可在「设置 → 通知」中撤销。',
        ],
      },
      {
        heading: '五、信息共享',
        body: [
          '我们不会出售或出租你的个人信息。',
          '我们不会向广告网络分享数据。',
          '仅在法律要求时，或为运营服务所必需（如服务器托管商）的服务提供商之间共享。这类提供商必须仅在我们的指示下使用数据。',
        ],
      },
      {
        heading: '六、信息保存期限',
        body: [
          '设备本地数据保留至你删除应用或在应用内清除。',
          '网站账户数据在你的账户存续期间保存；你可随时来信申请删除。',
          '支持邮件仅在解决你问题与遵守法律义务所必需的期限内保留。',
        ],
      },
      {
        heading: '七、你的权利',
        body: [
          '你可以访问、更正、导出或删除我们持有的关于你的个人信息。如需行使上述权利，请通过下方邮箱与我们联系，我们会在合理时间内回复。',
          '根据你所在的司法辖区（欧盟、英国、加州等），你可能还享有向监管机构投诉等额外权利。',
        ],
      },
      {
        heading: '八、儿童',
        body: [
          '醒醒鸭并非面向未满 13 周岁（或你所在司法辖区相应最低年龄）的儿童。我们不会有意收集儿童的个人信息。如果你认为有儿童向我们提交了个人信息，请联系我们以便删除。',
        ],
      },
      {
        heading: '九、跨境传输',
        body: [
          '我们的基础设施可能位于与你所在地区不同的国家或地区。在传输个人信息时，我们会依赖合同保障，并只与符合公认安全标准的服务商合作。',
        ],
      },
      {
        heading: '十、安全',
        body: [
          '我们对所有网络流量使用 HTTPS，对账户密码使用加盐哈希，并采取常规的服务器加固措施。没有任何系统是绝对安全的，如果你发现可疑情况，请立刻邮件告知。',
        ],
      },
      {
        heading: '十一、政策变更',
        body: [
          '我们可能会偶尔更新本政策，更新时会修改顶部生效日期。重大变更会在网站或应用中合适位置进行提示。',
        ],
      },
      {
        heading: '十二、联系我们',
        body: [
          `如对本政策有任何疑问，请邮件联系：${SUPPORT_EMAIL}。`,
        ],
      },
    ],
  },
};

function PrivacyPage({ lang }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];

  return (
    <section className="privacy-page">
      <header className="privacy-hero">
        <h1 className="privacy-title">{t.title}</h1>
        <p className="privacy-effective">{t.effective}</p>
        <p className="privacy-intro">{t.intro}</p>
      </header>

      <div className="privacy-card">
        {t.sections.map((section) => (
          <section className="privacy-section" key={section.heading}>
            <h2 className="privacy-section-title">{section.heading}</h2>
            {section.body.map((paragraph, idx) => (
              <p className="privacy-paragraph" key={idx}>
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>
    </section>
  );
}

export default PrivacyPage;
